import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  Text,
  VStack,
  Badge,
  useToast,
  Table,
  Tbody,
  Tr,
  Td,
  TableContainer,
  Avatar,
  Divider,
  GridItem,
  Icon,
  Stack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Skeleton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  SimpleGrid,
} from "@chakra-ui/react";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import { MdEmail, MdPhone } from "react-icons/md";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Loading } from "../../../components/Loading";
import { getCustomerFinanceDetails, getCustomerBookings, getCustomerPayments } from "../../../actions/finance/customerAccount.action";
import { FaRupeeSign, FaMoneyBillWave } from "react-icons/fa";
import { FiCheckCircle, FiXCircle } from "react-icons/fi";
import { GoChevronLeft, GoChevronRight } from "react-icons/go";
import axios from "axios";

const SERVER = import.meta.env.VITE_API_URL || "https://api.travelprym.in";

const CustomerFinanceDetails = () => {
  const toast = useToast();
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customerData, setCustomerData] = useState(null);
  const [financialData, setFinancialData] = useState(null);
  
  // Bookings pagination
  const [bookings, setBookings] = useState([]);
  const [bookingsPage, setBookingsPage] = useState(1);
  const [bookingsLimit] = useState(10);
  const [bookingsTotalPages, setBookingsTotalPages] = useState(1);
  const [bookingsTotal, setBookingsTotal] = useState(0);
  const [loadingBookings, setLoadingBookings] = useState(false);
  
  // Payments pagination
  const [payments, setPayments] = useState([]);
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [paymentsLimit] = useState(10);
  const [paymentsTotalPages, setPaymentsTotalPages] = useState(1);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [loadingPayments, setLoadingPayments] = useState(false);
  
  // Booking details modal
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [loadingBookingDetails, setLoadingBookingDetails] = useState(false);

  const getCustomerDetails = async () => {
    try {
      const res = await getCustomerFinanceDetails(id);
      if (res.success) {
        setCustomerData(res.data.customer);
        setFinancialData(res.data.financialSummary);
      }
    } catch (error) {
      console.error("Error fetching customer details:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to fetch customer details",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const loadBookings = async () => {
    setLoadingBookings(true);
    try {
      const res = await getCustomerBookings(id, bookingsPage, bookingsLimit);
      if (res.success) {
        setBookings(res.data);
        setBookingsTotalPages(res.pagination.totalPages);
        setBookingsTotal(res.pagination.total);
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to fetch bookings",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingBookings(false);
    }
  };

  const loadPayments = async () => {
    setLoadingPayments(true);
    try {
      const res = await getCustomerPayments(id, paymentsPage, paymentsLimit);
      if (res.success) {
        setPayments(res.data);
        setPaymentsTotalPages(res.pagination.totalPages);
        setPaymentsTotal(res.pagination.total);
      }
    } catch (error) {
      console.error("Error fetching payments:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to fetch payments",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingPayments(false);
    }
  };

  useEffect(() => {
    if (id) {
      getCustomerDetails();
      loadBookings();
      loadPayments();
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadBookings();
    }
  }, [bookingsPage]);

  useEffect(() => {
    if (id) {
      loadPayments();
    }
  }, [paymentsPage]);

  const handleBookingClick = async (booking) => {
    setSelectedBooking(null);
    setLoadingBookingDetails(true);
    onOpen();
    
    try {
      const token = localStorage.getItem("prima_session");
      if (!token) throw new Error("Auth token missing");

      const res = await axios.get(`${SERVER}/management/booking`, {
        params: { id: booking._id },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      if (res.data) {
        setSelectedBooking(res.data.data || res.data);
        // console.log("Fetched booking details:", res.data.data || res.data);
      }
    } catch (error) {
      console.error("Error fetching booking details:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to fetch booking details",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      onClose();
    } finally {
      setLoadingBookingDetails(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (!customerData) {
    return (
      <Box px={4} py={6}>
        <Text>Customer not found</Text>
      </Box>
    );
  }

  return (
    <Stack w={"100%"} justifyContent={"center"} spacing={6} p={6}>
      {/* Breadcrumbs */}
      <Flex
        justifyContent={{ base: "center", md: "space-between" }}
        alignItems={{ base: "flex-start", md: "center" }}
        flexDir={{ base: "column", md: "row" }}
        gap={4}
      >
        <BreadCrumbMaker
          items={[
            { link: "/finance", title: "Finance" },
            { link: "/finance/customer", title: "Customers" },
            {
              link: `/finance/customer/${id}`,
              title: customerData?.name || "Details",
              on: true,
            },
          ]}
        />

        {/* <Flex gap={2}>
          <Button
            onClick={() => navigate("/finance/customer")}
            colorScheme="gray"
            variant="outline"
            size="sm"
            borderRadius={5}
            px={6}
          >
            Back to List
          </Button>
        </Flex> */}
      </Flex>

      {/* Customer Info Card */}
      <Box bg="white" p={6} borderRadius="3xl" shadow="sm">
        <Flex
          alignItems="center"
          justifyContent="space-between"
          flexDirection={{ base: "column", md: "row" }}
          gap={4}
        >
          <Flex gap={4} alignItems="center">
            <Avatar
              name={customerData?.name}
              size="2xl"
              bg="blue.500"
              color="white"
            />
            <VStack alignItems={"flex-start"} spacing={2}>
              <Heading fontSize="2xl" color={"#1A202C"}>
                {customerData?.name}
              </Heading>
              <Text fontSize="sm" color={"gray.600"}>
                Customer ID: {customerData?.customerID}
              </Text>
              <Flex gap={3} flexWrap="wrap">
                <Badge
                  variant="subtle"
                  colorScheme={customerData?.type === "Individual" ? "blue" : "purple"}
                  fontSize="sm"
                  px={3}
                  py={1}
                >
                  {customerData?.type}
                </Badge>
                <Badge
                  variant="subtle"
                  colorScheme="yellow"
                  fontSize="sm"
                  px={3}
                  py={1}
                >
                  ⭐ {customerData?.rating || 0}
                </Badge>
              </Flex>
            </VStack>
          </Flex>

          <VStack alignItems={"flex-start"} spacing={2}>
            <Flex alignItems="center" gap={2}>
              <Icon as={MdPhone} color="blue.500" />
              <Text fontSize="sm">{customerData?.mobile || "N/A"}</Text>
            </Flex>
            <Flex alignItems="center" gap={2}>
              <Icon as={MdEmail} color="blue.500" />
              <Text fontSize="sm">{customerData?.email || "N/A"}</Text>
            </Flex>
          </VStack>
        </Flex>
      </Box>

      {/* Financial Stats Cards */}
      <Box>
        <Heading fontSize={"lg"} mb={4} color={"#333B69"}>
          Financial Summary
        </Heading>
        <Grid
          gridGap={6}
          templateColumns={{
            base: "repeat(2, 1fr)",
            md: "repeat(3, minmax(200px, 1fr))",
          }}
        >
          <GridItem>
            <Box
              bg="white"
              p={4}
              rounded="md"
              display="flex"
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
              h="110px"
              gap={2}
              borderRadius="3xl"
            >
              <Box
                bg={"#E7EDFF"}
                w={16}
                h={16}
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="full"
              >
                <Icon as={FiCheckCircle} boxSize={6} color={"#396AFF"} />
              </Box>
              <Flex flexDirection={"column"} alignItems={"flex-start"}>
                <Text fontSize="14px" color="#718EBF">
                  Total Bookings
                </Text>
                <Text fontSize="22px" fontWeight="600">
                  {financialData?.totalBookings || 0}
                </Text>
              </Flex>
            </Box>
          </GridItem>

          <GridItem>
            <Box
              bg="white"
              p={4}
              rounded="md"
              display="flex"
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
              h="110px"
              gap={2}
              borderRadius="3xl"
            >
              <Box
                bg={"#DCFCE7"}
                w={16}
                h={16}
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="full"
              >
                <Icon as={FaMoneyBillWave} boxSize={6} color={"#16A34A"} />
              </Box>
              <Flex flexDirection={"column"} alignItems={"flex-start"}>
                <Text fontSize="14px" color="#718EBF">
                  Total Fare (₹)
                </Text>
                <Text fontSize="22px" fontWeight="600">
                  ₹{(financialData?.totalFare || 0).toLocaleString()}
                </Text>
              </Flex>
            </Box>
          </GridItem>

          <GridItem>
            <Box
              bg="white"
              p={4}
              rounded="md"
              display="flex"
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
              h="110px"
              gap={2}
              borderRadius="3xl"
            >
              <Box
                bg={"#FFF5D9"}
                w={16}
                h={16}
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="full"
              >
                <Icon as={FaRupeeSign} boxSize={6} color={"#FFBB38"} />
              </Box>
              <Flex flexDirection={"column"} alignItems={"flex-start"}>
                <Text fontSize="14px" color="#718EBF">
                  Total Paid (₹)
                </Text>
                <Text fontSize="22px" fontWeight="600" color="green.600">
                  ₹{(financialData?.totalPaymentPaid || 0).toLocaleString()}
                </Text>
              </Flex>
            </Box>
          </GridItem>

          <GridItem>
            <Box
              bg="white"
              p={4}
              rounded="md"
              display="flex"
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
              h="110px"
              gap={2}
              borderRadius="3xl"
            >
              <Box
                bg={"#FECACA"}
                w={16}
                h={16}
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="full"
              >
                <Icon as={FiXCircle} boxSize={6} color={"#DC2626"} />
              </Box>
              <Flex flexDirection={"column"} alignItems={"flex-start"}>
                <Text fontSize="14px" color="#718EBF">
                  Remaining (₹)
                </Text>
                <Text fontSize="22px" fontWeight="600" color="red.600">
                  ₹{(financialData?.remainingAmount || 0).toLocaleString()}
                </Text>
              </Flex>
            </Box>
          </GridItem>

          <GridItem>
            <Box
              bg="white"
              p={4}
              rounded="md"
              display="flex"
              flexDirection="row"
              justifyContent="flex-start"
              alignItems="center"
              h="110px"
              gap={2}
              borderRadius="3xl"
            >
              <Box
                bg={"#E0E7FF"}
                w={16}
                h={16}
                display="flex"
                alignItems="center"
                justifyContent="center"
                borderRadius="full"
              >
                <Icon as={FiCheckCircle} boxSize={6} color={"#4F46E5"} />
              </Box>
              <Flex flexDirection={"column"} alignItems={"flex-start"}>
                <Text fontSize="14px" color="#718EBF">
                  Completed
                </Text>
                <Text fontSize="22px" fontWeight="600">
                  {financialData?.completedBookings || 0}
                </Text>
              </Flex>
            </Box>
          </GridItem>
        </Grid>
      </Box>

      {/* Tabs for All Bookings and Payments */}
      <Box bg="white" p={6} borderRadius="3xl">
        <Tabs colorScheme="blue">
          <TabList>
            <Tab>All Bookings</Tab>
            <Tab>All Payments</Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              {loadingBookings ? (
                <Stack spacing={3} p={4}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} height="60px" borderRadius="md" />
                  ))}
                </Stack>
              ) : (
                <>
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Tbody>
                        {bookings && bookings.length > 0 ? (
                          <>
                            <Tr bg="gray.50">
                              <Td fontWeight="600">Booking ID</Td>
                              <Td fontWeight="600">Type</Td>
                              <Td fontWeight="600">Date</Td>
                              <Td fontWeight="600">Pickup</Td>
                              <Td fontWeight="600">Drop</Td>
                              <Td fontWeight="600">Fare</Td>
                              <Td fontWeight="600">Status</Td>
                            </Tr>
                            {bookings.map((booking, idx) => (
                              <Tr 
                                key={idx}
                                cursor="pointer"
                                _hover={{ bg: "gray.100" }}
                                onClick={() => handleBookingClick(booking)}
                              >
                                <Td>{booking.bookingID}</Td>
                                <Td>
                                  <Badge colorScheme={booking.bookingType === "custom" ? "purple" : "blue"}>
                                    {booking.bookingType || "regular"}
                                  </Badge>
                                </Td>
                                <Td>{new Date(booking.dateTime).toLocaleDateString()}</Td>
                                <Td>{booking.pickupLocation}</Td>
                                <Td>{booking.dropLocation}</Td>
                                <Td>₹{booking.fare?.toLocaleString()}</Td>
                                <Td>
                                  <Badge colorScheme={
                                    booking.status === "Completed" ? "green" :
                                    booking.status === "Cancelled" || booking.status === "Expired" ? "red" :
                                    "yellow"
                                  }>
                                    {booking.status}
                                  </Badge>
                                </Td>
                              </Tr>
                            ))}
                          </>
                        ) : (
                          <Tr>
                            <Td colSpan={7} textAlign="center">
                              No bookings found
                            </Td>
                          </Tr>
                        )}
                      </Tbody>
                    </Table>
                  </TableContainer>
                  
                  {/* Bookings Pagination */}
                  {bookingsTotalPages > 1 && (
                    <Flex justify="space-between" align="center" mt={4} px={2}>
                      <Text fontSize="sm" color="gray.600">
                        Page {bookingsPage} of {bookingsTotalPages} ({bookingsTotal} total bookings)
                      </Text>
                      <Flex gap={2}>
                        <Button
                          size="sm"
                          leftIcon={<GoChevronLeft />}
                          isDisabled={bookingsPage === 1}
                          onClick={() => setBookingsPage(bookingsPage - 1)}
                          colorScheme="blue"
                          variant="outline"
                        >
                          Previous
                        </Button>
                        <Button
                          size="sm"
                          rightIcon={<GoChevronRight />}
                          isDisabled={bookingsPage === bookingsTotalPages}
                          onClick={() => setBookingsPage(bookingsPage + 1)}
                          colorScheme="blue"
                          variant="outline"
                        >
                          Next
                        </Button>
                      </Flex>
                    </Flex>
                  )}
                </>
              )}
            </TabPanel>

            <TabPanel>
              {loadingPayments ? (
                <Stack spacing={3} p={4}>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} height="60px" borderRadius="md" />
                  ))}
                </Stack>
              ) : (
                <>
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Tbody>
                        {payments && payments.length > 0 ? (
                          <>
                            <Tr bg="gray.50">
                              <Td fontWeight="600">Date</Td>
                              <Td fontWeight="600">Amount</Td>
                              <Td fontWeight="600">Type</Td>
                              <Td fontWeight="600">Status</Td>
                            </Tr>
                            {payments.map((payment, idx) => (
                              <Tr key={idx}>
                                <Td>{new Date(payment.date).toLocaleDateString()}</Td>
                                <Td>₹{payment.amount?.toLocaleString()}</Td>
                                <Td>{payment.type}</Td>
                                <Td>
                                  <Badge colorScheme={payment.status === "Paid" ? "green" : "orange"}>
                                    {payment.status}
                                  </Badge>
                                </Td>
                              </Tr>
                            ))}
                          </>
                        ) : (
                          <Tr>
                            <Td colSpan={4} textAlign="center">
                              No payments found
                            </Td>
                          </Tr>
                        )}
                      </Tbody>
                    </Table>
                  </TableContainer>
                  
                  {/* Payments Pagination */}
                  {paymentsTotalPages > 1 && (
                    <Flex justify="space-between" align="center" mt={4} px={2}>
                      <Text fontSize="sm" color="gray.600">
                        Page {paymentsPage} of {paymentsTotalPages} ({paymentsTotal} total payments)
                      </Text>
                      <Flex gap={2}>
                        <Button
                          size="sm"
                          leftIcon={<GoChevronLeft />}
                          isDisabled={paymentsPage === 1}
                          onClick={() => setPaymentsPage(paymentsPage - 1)}
                          colorScheme="blue"
                          variant="outline"
                        >
                          Previous
                        </Button>
                        <Button
                          size="sm"
                          rightIcon={<GoChevronRight />}
                          isDisabled={paymentsPage === paymentsTotalPages}
                          onClick={() => setPaymentsPage(paymentsPage + 1)}
                          colorScheme="blue"
                          variant="outline"
                        >
                          Next
                        </Button>
                      </Flex>
                    </Flex>
                  )}
                </>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      {/* Booking Details Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Booking Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {loadingBookingDetails ? (
              <Stack spacing={4} p={4}>
                <Skeleton height="40px" borderRadius="md" />
                <Skeleton height="200px" borderRadius="md" />
                <Skeleton height="150px" borderRadius="md" />
                <Skeleton height="150px" borderRadius="md" />
                <Skeleton height="120px" borderRadius="md" />
              </Stack>
            ) : selectedBooking ? (
              <Stack spacing={6}>
                {/* Booking ID and Status */}
                <Flex justify="space-between" align="center">
                  <Box>
                    <Text fontSize="sm" color="gray.600">Booking ID</Text>
                    <Text fontSize="xl" fontWeight="600">{selectedBooking.bookingID}</Text>
                  </Box>
                  <Badge 
                    fontSize="md" 
                    px={3} 
                    py={1}
                    colorScheme={
                      selectedBooking.status === "Completed" ? "green" :
                      selectedBooking.status === "Cancelled" || selectedBooking.status === "Expired" ? "red" :
                      selectedBooking.status === "Ongoing" ? "blue" :
                      "yellow"
                    }
                  >
                    {selectedBooking.status}
                  </Badge>
                </Flex>

                <Divider />

                {/* Trip Details */}
                <Box>
                  <Heading size="sm" mb={3} color="#333B69">Trip Information</Heading>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <Box>
                      <Text fontSize="xs" color="gray.600">Pickup Location</Text>
                      <Text fontSize="sm" fontWeight="500">{selectedBooking.pickupLocation || "-"}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.600">Drop Location</Text>
                      <Text fontSize="sm" fontWeight="500">{selectedBooking.dropLocation || "-"}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.600">Date & Time</Text>
                      <Text fontSize="sm" fontWeight="500">
                        {selectedBooking.dateTime ? new Date(selectedBooking.dateTime).toLocaleString() : "-"}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.600">Booking Type</Text>
                      <Badge colorScheme={selectedBooking.bookingType === "custom" ? "purple" : "blue"}>
                        {selectedBooking.bookingType || "regular"}
                      </Badge>
                    </Box>
                    {selectedBooking.tripType && (
                      <Box>
                        <Text fontSize="xs" color="gray.600">Trip Type</Text>
                        <Text fontSize="sm" fontWeight="500">{selectedBooking.tripType}</Text>
                      </Box>
                    )}
                    {selectedBooking.distance && (
                      <Box>
                        <Text fontSize="xs" color="gray.600">Distance</Text>
                        <Text fontSize="sm" fontWeight="500">{selectedBooking.distance} km</Text>
                      </Box>
                    )}
                  </SimpleGrid>
                </Box>

                <Divider />

                {/* Customer Details */}
                <Box>
                  <Heading size="sm" mb={3} color="#333B69">Customer Information</Heading>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <Box>
                      <Text fontSize="xs" color="gray.600">Name</Text>
                      <Text fontSize="sm" fontWeight="500">{selectedBooking.customer?.name || "-"}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.600">Mobile</Text>
                      <Text fontSize="sm" fontWeight="500">{selectedBooking.customer?.mobile || "-"}</Text>
                    </Box>
                    {selectedBooking.customer?.email && (
                      <Box>
                        <Text fontSize="xs" color="gray.600">Email</Text>
                        <Text fontSize="sm" fontWeight="500">{selectedBooking.customer.email}</Text>
                      </Box>
                    )}
                  </SimpleGrid>
                </Box>

                <Divider />

                {/* Driver Details */}
                {selectedBooking.driver && (
                  <>
                    <Box>
                      <Heading size="sm" mb={3} color="#333B69">Driver Information</Heading>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <Box>
                          <Text fontSize="xs" color="gray.600">Name</Text>
                          <Text fontSize="sm" fontWeight="500">{selectedBooking.driver?.name || "-"}</Text>
                        </Box>
                        <Box>
                          <Text fontSize="xs" color="gray.600">Mobile</Text>
                          <Text fontSize="sm" fontWeight="500">
                            {selectedBooking.driver?.contactDetails?.mobileNumber || "-"}
                          </Text>
                        </Box>
                      </SimpleGrid>
                    </Box>
                    <Divider />
                  </>
                )}

                {/* Vehicle Details */}
                {selectedBooking.carDetails && (
                  <>
                    <Box>
                      <Heading size="sm" mb={3} color="#333B69">Vehicle Information</Heading>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        <Box>
                          <Text fontSize="xs" color="gray.600">Car Type</Text>
                          <Text fontSize="sm" fontWeight="500">{selectedBooking.carDetails?.name || "-"}</Text>
                        </Box>
                        {selectedBooking.carDetails?.registrationNumber && (
                          <Box>
                            <Text fontSize="xs" color="gray.600">Registration Number</Text>
                            <Text fontSize="sm" fontWeight="500">{selectedBooking.carDetails.registrationNumber}</Text>
                          </Box>
                        )}
                      </SimpleGrid>
                    </Box>
                    <Divider />
                  </>
                )}

                {/* Fare Details */}
                <Box>
                  <Heading size="sm" mb={3} color="#333B69">Fare Breakdown</Heading>
                  <Stack spacing={2} bg="gray.50" p={4} borderRadius="md">
                    <Flex justify="space-between">
                      <Text fontSize="sm" color="gray.700">Base Fare</Text>
                      <Text fontSize="sm" fontWeight="500">
                        ₹{selectedBooking.fareDetails?.baseFare?.toLocaleString() || 0}
                      </Text>
                    </Flex>
                    {selectedBooking.fareDetails?.discounts > 0 && (
                      <Flex justify="space-between">
                        <Text fontSize="sm" color="green.600">Discount</Text>
                        <Text fontSize="sm" fontWeight="500" color="green.600">
                          - ₹{selectedBooking.fareDetails.discounts?.toLocaleString()}
                        </Text>
                      </Flex>
                    )}
                    <Divider />
                    <Flex justify="space-between">
                      <Text fontSize="md" fontWeight="600" color="#333B69">Total Fare</Text>
                      <Text fontSize="md" fontWeight="600" color="#333B69">
                        ₹{selectedBooking.fareDetails?.total?.toLocaleString() || 0}
                      </Text>
                    </Flex>
                  </Stack>
                </Box>

                {/* Payment Status */}
                {selectedBooking.payments && selectedBooking.payments.length > 0 && (
                  <>
                    <Divider />
                    <Box>
                      <Heading size="sm" mb={3} color="#333B69">Payment History</Heading>
                      <Stack spacing={2}>
                        {selectedBooking.payments.map((payment, idx) => (
                          <Flex 
                            key={idx} 
                            justify="space-between" 
                            align="center"
                            p={3}
                            bg="gray.50"
                            borderRadius="md"
                          >
                            <Box>
                              <Text fontSize="sm" fontWeight="500">
                                {payment.type || "Payment"}
                              </Text>
                              <Text fontSize="xs" color="gray.600">
                                {payment.createdAt ? new Date(payment.createdAt).toLocaleString() : "-"}
                              </Text>
                            </Box>
                            <Flex align="center" gap={3}>
                              <Text fontSize="sm" fontWeight="600">
                                ₹{payment.amount?.toLocaleString() || 0}
                              </Text>
                              <Badge colorScheme={payment.status === "Paid" ? "green" : "orange"}>
                                {payment.status}
                              </Badge>
                            </Flex>
                          </Flex>
                        ))}
                      </Stack>
                    </Box>
                  </>
                )}
              </Stack>
            ) : (
              <Text textAlign="center" color="gray.500">No booking details available</Text>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Stack>
  );
};

export default CustomerFinanceDetails;
